// features/landing/components/MobileQuickActions.tsx
/**
 * MobileQuickActions - Role-based quick action buttons for mobile users
 *
 * OPTIMIZED: Uses tournamentMetadata (~1KB) instead of full tournaments (~5MB+)
 * - Subscribes to lightweight /tournamentMetadata collection
 * - Appeals counted via granular /tournaments/{id}/appeals subscriptions
 * - Penalties shown on Penalty page (requires full player download ~5MB)
 *
 * Shows quick access buttons based on user role and team assignments:
 * - Floor Judge: Table Management, Players, Timer Overview
 * - Team Lead: Same + Penalty Approval (with notification)
 * - Feature Match: Deck Check, Players
 * - Scorekeeper: Tournament, Problem Tracker, Timer Overview
 * - Head Judge: Table Management, Appeal, Penalty Approval, Timer Overview
 * - Management: Tournament, Staff Dashboard, Timer Overview, Problem Tracker
 *
 * Special modifiers:
 * - DC team assignment: DC button instead of table management
 * - FM team assignment: Same as DC
 * - HJ team assignment: Get HJ-level buttons
 */
import { ReactElement, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Badge, SvgIconProps } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import TimerIcon from '@mui/icons-material/Timer';
import WarningIcon from '@mui/icons-material/Warning';
import GavelIcon from '@mui/icons-material/Gavel';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BugReportIcon from '@mui/icons-material/BugReport';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { ref, onValue, Unsubscribe } from 'firebase/database';
import { db } from '../../../config/firebase';
import { ROUTES } from '../../../config/routes';
import { ROLES } from '../../../config/constants';
import type { UserData } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useTournamentMetadata } from '../../../hooks/useTournamentMetadata';

// ============================================================================
// TYPES
// ============================================================================

// Lightweight tournament type - only metadata fields needed
interface TournamentMetadata {
  id: string;
  name?: string;
  status?: string;
  isPublic?: boolean;
  staffAssignments?: Record<string, unknown>;
  allowedRoles?: string[];
  floorTeams?: Record<string, FloorTeam>;
}

interface Appeal {
  status?: string;
}

interface FloorTeam {
  teamLeadId?: string;
}

interface QuickActionButtonProps {
  icon: React.ComponentType<SvgIconProps>;
  label: string;
  onClick: () => void;
  color?: string;
  badge?: number;
}

interface QuickActionItem {
  key: string;
  icon: React.ComponentType<SvgIconProps>;
  label: string;
  color: string;
  route: string;
  badge?: number;
}

interface MobileQuickActionsProps {
  user: UserData;
}

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * QuickActionButton - Individual action button with icon and label
 */
function QuickActionButton({ icon: Icon, label, onClick, color = '#00FFD4', badge = 0 }: QuickActionButtonProps): ReactElement {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        p: 1,
        borderRadius: 1.5,
        cursor: 'pointer',
        minWidth: 56,
        flex: 1,
        maxWidth: 80,
        transition: 'all 0.15s ease',
        '&:active': {
          transform: 'scale(0.92)',
          backgroundColor: `${color}20`,
        },
      }}
    >
      <Badge
        badgeContent={badge}
        color="error"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: 'error.main',
            color: 'text.primary',
            fontWeight: 700,
            fontSize: '0.6rem',
            minWidth: 14,
            height: 14,
            top: 2,
            right: 2,
          },
        }}
      >
        <Icon sx={{ color, fontSize: '1.5rem' }} />
      </Badge>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.6rem', textAlign: 'center', lineHeight: 1.1, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * MobileQuickActions - Container for quick action buttons
 */
export function MobileQuickActions({ user }: MobileQuickActionsProps): ReactElement | null {
  const navigate = useNavigate();
  const { tournaments: metadata } = useTournamentMetadata();
  const [pendingAppeals, setPendingAppeals] = useState<number>(0);
  // Penalties set to 0 - counting requires downloading all players (~5MB)
  // Penalties badge is shown on the dedicated Penalty Approval page instead
  const pendingPenalties = 0;

  const role = user?.role || '';

  // isAdmin goes through usePermissions so accountType==='admin'
  // is honoured alongside the legacy role string — the original
  // role-only check silently mis-classified admins whose record
  // had been migrated to the accountType-based shape. Other role
  // flags below stay role-only since they're already fed by the
  // tournament-runner's effectiveRole and haven't been migrated
  // off the legacy string yet.
  const { isAdmin } = usePermissions();
  const isManager = role === ROLES.MANAGER;
  const isHeadJudge = role === ROLES.HEAD_JUDGE;
  const isScorekeeper = role === ROLES.SCOREKEEPER;
  const isTeamLead = role === ROLES.TEAM_LEAD;
  const isFloorJudge = role === ROLES.FLOOR_JUDGE;
  const isFeatureMatch = role === ROLES.FEATURE_MATCH_JUDGE;
  const isShield = role === ROLES.SHIELD;

  // Team assignments that modify button visibility
  const isDeckCheckTeam = user?.deckCheckTeamAssignments && Object.keys(user.deckCheckTeamAssignments).length > 0;
  const isFeatureMatchTeam = user?.featureMatchTeamAssignments && Object.keys(user.featureMatchTeamAssignments).length > 0;
  const isHeadJudgeTeam = user?.headJudgeTeamAssignments && Object.keys(user.headJudgeTeamAssignments).length > 0;

  // Determine what data we need to fetch
  const needsAppeals = isAdmin || isManager || isHeadJudge || isHeadJudgeTeam;

  // Visibility filter — same shape as the rule's read gating, so
  // we don't fan out per-tournament reads against a tournament the
  // user can't access. Memoised on metadata + user.uid + role so
  // the appeals subscription effect below doesn't re-mount on
  // every component re-render.
  const tournaments = useMemo<TournamentMetadata[]>(() => {
    if (!user?.uid) return [];
    const allTournaments: TournamentMetadata[] = metadata.map((m) => ({
      id: m.id,
      name: m.name,
      status: m.status,
      isPublic: m.isPublic,
      staffAssignments: m.staffAssignments,
      allowedRoles: m.allowedRoles,
      floorTeams: m.floorTeams as TournamentMetadata['floorTeams'],
    }));
    if (['admin', 'Manager', 'Scorekeeper'].includes(role)) {
      return allTournaments;
    }
    return allTournaments.filter((t) => {
      if (t.isPublic) return true;
      if (t.staffAssignments?.[user?.uid || '']) return true;
      if (t.allowedRoles?.includes(role)) return true;
      return false;
    });
  }, [metadata, user?.uid, role]);

  // GRANULAR APPEALS: Subscribe to each tournament's appeals separately
  // Downloads only appeals data (~few KB) instead of entire tournaments (~5MB+)
  useEffect(() => {
    if (!needsAppeals || tournaments.length === 0) {
      setPendingAppeals(0);
      return;
    }

    const appealCounts = new Map<string, number>();
    const unsubscribes: Unsubscribe[] = [];

    const updateTotalAppeals = () => {
      let total = 0;
      appealCounts.forEach(count => { total += count; });
      setPendingAppeals(total);
    };

    // Subscribe to appeals for each active tournament
    tournaments.forEach(tournament => {
      const appealsRef = ref(db, `tournaments/${tournament.id}/appeals`);
      const unsub = onValue(appealsRef, (snapshot) => {
        const appeals = snapshot.val() as Record<string, Appeal> | null;
        if (!appeals) {
          appealCounts.set(tournament.id, 0);
        } else {
          const pendingCount = Object.values(appeals).filter(
            appeal => appeal.status === 'open' || appeal.status === 'in_progress'
          ).length;
          appealCounts.set(tournament.id, pendingCount);
        }
        updateTotalAppeals();
      });
      unsubscribes.push(unsub);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [needsAppeals, tournaments]);

  // Check if user has any tournament assignments (for non-management roles)
  const hasTournamentAssignment = tournaments.some(t =>
    t.staffAssignments?.[user?.uid || ''] ||
    user?.deckCheckTeamAssignments?.[t.id] ||
    user?.featureMatchTeamAssignments?.[t.id] ||
    user?.headJudgeTeamAssignments?.[t.id]
  );

  // Show quick actions for all staff roles
  const isRecognizedStaffRole = isAdmin || isManager || isScorekeeper || isHeadJudge || isTeamLead || isFloorJudge || isFeatureMatch || isShield;
  const showQuickActions = isRecognizedStaffRole || hasTournamentAssignment;

  // Don't render if user has no tournament assignments (for lower roles)
  if (!showQuickActions) return null;

  // Build the list of quick action buttons based on role
  const getQuickActions = (): QuickActionItem[] => {
    const actions: QuickActionItem[] = [];

    // Management: Tournament, Staff Dashboard, Timer Overview, Problem Tracker
    if (isAdmin || isManager) {
      actions.push({ key: 'tournament', icon: EmojiEventsIcon, label: 'Tournament', color: '#FFE600', route: ROUTES.TOURNAMENT_DASHBOARD });
      actions.push({ key: 'staff', icon: ManageAccountsIcon, label: 'Staff', color: '#7B68EE', route: ROUTES.STAFF_DASHBOARD });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      actions.push({ key: 'problems', icon: BugReportIcon, label: 'Problems', color: '#FF6B35', route: ROUTES.PROBLEM_TRACKER_DASHBOARD });
      return actions;
    }

    // Scorekeeper: Tournament, Problem Tracker, Timer Overview
    if (isScorekeeper) {
      actions.push({ key: 'tournament', icon: EmojiEventsIcon, label: 'Tournament', color: '#FFE600', route: ROUTES.TOURNAMENT_DASHBOARD });
      actions.push({ key: 'problems', icon: BugReportIcon, label: 'Problems', color: '#FF6B35', route: ROUTES.PROBLEM_TRACKER_DASHBOARD });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      return actions;
    }

    // Head Judge (or HJ team): Table Management, Appeal, Penalty Approval, Timer Overview
    if (isHeadJudge || isHeadJudgeTeam) {
      actions.push({ key: 'tables', icon: TableChartIcon, label: 'Tables', color: '#00FFD4', route: ROUTES.TABLE_MANAGEMENT_DASHBOARD });
      actions.push({ key: 'appeals', icon: GavelIcon, label: 'Appeals', color: '#FFE600', route: ROUTES.APPEALS, badge: pendingAppeals });
      actions.push({ key: 'penalties', icon: WarningIcon, label: 'Penalties', color: '#FF3366', route: ROUTES.PENALTY_APPROVAL, badge: pendingPenalties });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      return actions;
    }

    // Feature Match: Deck Check, Players
    if (isFeatureMatch) {
      actions.push({ key: 'deckcheck', icon: FactCheckIcon, label: 'Deck Check', color: '#7B68EE', route: ROUTES.DECK_CHECK_DASHBOARD });
      actions.push({ key: 'players', icon: PeopleIcon, label: 'Players', color: '#00D4FF', route: ROUTES.PLAYERS_DASHBOARD });
      return actions;
    }

    // Team Lead: Table Management (or DC/FM if assigned), Players, Timer Overview, Penalty Approval
    if (isTeamLead) {
      // Check for DC or FM team assignment
      if (isDeckCheckTeam || isFeatureMatchTeam) {
        actions.push({ key: 'deckcheck', icon: FactCheckIcon, label: 'Deck Check', color: '#7B68EE', route: ROUTES.DECK_CHECK_DASHBOARD });
      } else {
        actions.push({ key: 'tables', icon: TableChartIcon, label: 'Tables', color: '#00FFD4', route: ROUTES.TABLE_MANAGEMENT_DASHBOARD });
      }
      actions.push({ key: 'players', icon: PeopleIcon, label: 'Players', color: '#00D4FF', route: ROUTES.PLAYERS_DASHBOARD });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      actions.push({ key: 'penalties', icon: WarningIcon, label: 'Penalties', color: '#FF3366', route: ROUTES.PENALTY_APPROVAL, badge: pendingPenalties });
      return actions;
    }

    // Floor Judge: Table Management (or DC/FM if assigned), Players, Timer Overview
    if (isFloorJudge) {
      // Check for DC or FM team assignment
      if (isDeckCheckTeam || isFeatureMatchTeam) {
        actions.push({ key: 'deckcheck', icon: FactCheckIcon, label: 'Deck Check', color: '#7B68EE', route: ROUTES.DECK_CHECK_DASHBOARD });
      } else {
        actions.push({ key: 'tables', icon: TableChartIcon, label: 'Tables', color: '#00FFD4', route: ROUTES.TABLE_MANAGEMENT_DASHBOARD });
      }
      actions.push({ key: 'players', icon: PeopleIcon, label: 'Players', color: '#00D4FF', route: ROUTES.PLAYERS_DASHBOARD });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      return actions;
    }

    // Shield: Players, Timer Overview
    if (isShield) {
      actions.push({ key: 'players', icon: PeopleIcon, label: 'Players', color: '#00D4FF', route: ROUTES.PLAYERS_DASHBOARD });
      actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });
      return actions;
    }

    // Default: show basic actions
    actions.push({ key: 'tables', icon: TableChartIcon, label: 'Tables', color: '#00FFD4', route: ROUTES.TABLE_MANAGEMENT_DASHBOARD });
    actions.push({ key: 'players', icon: PeopleIcon, label: 'Players', color: '#00D4FF', route: ROUTES.PLAYERS_DASHBOARD });
    actions.push({ key: 'timer', icon: TimerIcon, label: 'Timer', color: '#00FFD4', route: ROUTES.TIMER_OVERVIEW });

    return actions;
  };

  const quickActions = getQuickActions();

  if (quickActions.length === 0) return null;

  return (
    <Box
      sx={{
        display: { xs: 'block', sm: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#151B23',
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        px: 1,
        py: 1,
        zIndex: 1100,
        // Safe area for iOS
        paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: 500,
          mx: 'auto',
        }}
      >
        {quickActions.map((action) => (
          <QuickActionButton
            key={action.key}
            icon={action.icon}
            label={action.label}
            color={action.color}
            badge={action.badge || 0}
            onClick={() => navigate(action.route)}
          />
        ))}
      </Box>
    </Box>
  );
}

export default MobileQuickActions;
